import { DomainsIcon } from "@minket/brand/icons/domains";
import { Button } from "@minket/ui/components/button";
import { EmptyState } from "@minket/ui/components/empty-state";
import { type UseQueryResult, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense, useState } from "react";
import type { DomainDialog } from "@/components/domains/actions.ondemand";
import type { ConsoleDomain } from "@/components/domains/add-domain-form.ondemand";
import { DmarcLadder } from "@/components/domains/dmarc";
import { DomainHead } from "@/components/domains/head";
import { headline } from "@/components/domains/headline";
import { BimiSection, ReputationSection } from "@/components/domains/panels";
import { RecordsLedger } from "@/components/domains/records";
import { UnavailableAction } from "@/components/page/action";
import { GhostSections } from "@/components/page/ghost";
import { Page } from "@/components/page/page";
import { ProjectScope } from "@/components/page/scope";
import { Section, Sheet } from "@/components/page/section";
import { QueryView } from "@/components/page/states";
import { describeError, isNotFound } from "@/lib/api";
import { endpoints } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import { apiQuery, apiRequest } from "@/lib/query";
import { notify } from "@/lib/toast";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/domains/$id")({
  component: DomainDetail,
});

/** The countersigns and the BIMI form load on first use, with the writes they make. */
const DomainDialogs = lazy(() =>
  import("@/components/domains/actions.ondemand").then((m) => ({ default: m.DomainDialogs })),
);

const Autoconfigure = lazy(() =>
  import("@/components/domains/autoconfigure.ondemand").then((m) => ({ default: m.Autoconfigure })),
);

/**
 * Automatic DNS setup, asked of the domain's DNS provider as soon as a record mail needs is
 * missing (and on the way back from the provider), so a Cloudflare zone shows its button at once.
 */
function DnsSetup(props: { domain: ConsoleDomain; canManage: boolean; onFix: () => void }) {
  const missing = props.domain.records.some(
    (record) => record.purpose !== "tracking" && record.status !== "verified",
  );
  if (!missing && !window.location.search.includes("setup=")) return null;
  return (
    <Suspense fallback="Asking your DNS provider…">
      <Autoconfigure {...props} />
    </Suspense>
  );
}

const sections = [
  { title: "DNS records", description: "Each record's name, value and live check.", wide: true },
  { title: "DMARC", description: "Tightened one step at a time." },
  { title: "Reputation", description: "Postmaster and the seed tests." },
];
const will = "The domain's seal, its records and their checks will show here.";

function DomainDetail() {
  const { id } = Route.useParams();
  const { project, user } = useWorkspace();
  const canManage = useCan("domains.manage");
  const queryClient = useQueryClient();
  const params = { projectId: project?.id ?? "", domainId: id };
  const query = useQuery({ ...apiQuery(endpoints.domain, params), enabled: !!project });
  const domain = query.data;
  const [dialog, setDialog] = useState<DomainDialog | null>(null);
  const [checking, setChecking] = useState(false);

  const show = (next: ConsoleDomain) =>
    queryClient.setQueryData(apiQuery(endpoints.domain, params).queryKey, next);
  const check = async () => {
    setChecking(true);
    try {
      const next = await apiRequest(endpoints.verifyDomain, params);
      show(next);
      const verified = next.records.filter((r) => r.status === "verified").length;
      notify("success", `Checked: ${verified} of ${next.records.length} records verified.`);
    } catch (error) {
      notify("error", describeError(error).message);
    } finally {
      setChecking(false);
    }
  };

  const unverified = domain?.records.some((record) => record.status !== "verified") ?? false;
  const writes = !!domain?.cloudflare_zone_id && unverified;
  return (
    <Page
      title={domain?.name ?? "Domain"}
      documentTitle={domain ? `${domain.name} · Domains` : `Domain ${id}`}
      serial={{ id, label: "domain id" }}
      back={{ to: "/domains", label: "Domains" }}
      description={domain ? headline(domain) : undefined}
      action={
        !project || !domain ? null : !canManage ? (
          <UnavailableAction label="Check DNS" reason="Viewers cannot check DNS" />
        ) : (
          <>
            <Button variant={writes ? "outline" : "default"} loading={checking} onClick={check}>
              Check DNS
            </Button>
            {writes ? <Button onClick={() => setDialog({ kind: "fix" })}>Fix DNS</Button> : null}
          </>
        )
      }
    >
      <ProjectScope
        will={will}
        icon={<DomainsIcon />}
        preview={(busy) => <GhostSections sections={sections} busy={busy} />}
      >
        {(scope) => (
          <DomainBody
            query={query}
            projectName={scope.name}
            canManage={canManage}
            onDialog={setDialog}
          />
        )}
      </ProjectScope>
      {domain && dialog ? (
        <Suspense fallback={null}>
          <DomainDialogs
            dialog={dialog}
            domain={domain}
            projectId={params.projectId}
            signer={user.name ?? user.email}
            onClose={() => setDialog(null)}
          />
        </Suspense>
      ) : null}
    </Page>
  );
}

function DomainBody({
  query,
  projectName,
  canManage,
  onDialog,
}: {
  query: UseQueryResult<ConsoleDomain>;
  projectName: string;
  canManage: boolean;
  onDialog: (dialog: DomainDialog) => void;
}) {
  if (query.isError && isNotFound(query.error)) {
    return (
      <EmptyState
        data-state="not-found"
        icon={<DomainsIcon />}
        title="Domain not found"
        description={`Nothing with this id exists in ${projectName}. It may belong to another project, or the link is wrong.`}
      >
        <Button asChild variant="outline">
          <Link to="/domains">Back to domains</Link>
        </Button>
      </EmptyState>
    );
  }
  return (
    <QueryView
      query={query}
      noun="this domain"
      preview={(busy) => <GhostSections sections={sections} busy={busy} />}
    >
      {(domain) => <DomainView domain={domain} canManage={canManage} onDialog={onDialog} />}
    </QueryView>
  );
}

function DomainView({
  domain,
  canManage,
  onDialog,
}: {
  domain: ConsoleDomain;
  canManage: boolean;
  onDialog: (dialog: DomainDialog) => void;
}) {
  const [active, setActive] = useState<string | null>(null);
  const pick = (recordId: string) => {
    const row = document.getElementById(`record-${recordId}`);
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    row?.scrollIntoView({ block: "center", behavior: still ? "auto" : "smooth" });
    row?.focus({ preventScroll: true });
  };
  return (
    <div className="grid min-w-0 gap-10">
      <DomainHead domain={domain} active={active} onActive={setActive} onPick={pick} />
      <Sheet>
        <Section wide title="DNS records">
          {domain.cloudflare_zone_id ? null : (
            <p data-slot="dns-setup" className="max-w-[72ch] text-sm text-muted-foreground">
              <DnsSetup
                domain={domain}
                canManage={canManage}
                onFix={() => onDialog({ kind: "fix" })}
              />
            </p>
          )}
          <RecordsLedger records={domain.records} active={active} onActive={setActive} />
        </Section>
        <DmarcLadder
          domain={domain}
          canManage={canManage}
          onMove={(to) => onDialog({ kind: "dmarc", to })}
        />
        <ReputationSection domain={domain} />
        <BimiSection
          domain={domain}
          canManage={canManage}
          onEdit={() => onDialog({ kind: "bimi" })}
        />
        {canManage ? (
          <Section
            title="Remove"
            description="Sent mail keeps its history."
            action={
              <Button variant="destructive" onClick={() => onDialog({ kind: "remove" })}>
                Remove domain
              </Button>
            }
          />
        ) : (
          <p className="text-sm text-muted-foreground">
            Viewers can read domains but not change them.
          </p>
        )}
      </Sheet>
    </div>
  );
}
